"use client"

import React, { useState } from "react"
import { loadRefreshedBaskeAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { useTranslations } from "next-intl"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import { cn } from "@/lib/utils"
import { Label, Select, SelectContent, SelectItem, SelectTrigger, Switch } from "@/components/ui/components"
import StoryblokCustomerTypeDialogClient from "@/components/storyblok/header/StoryblokCustomerTypeDialogClient"

export default function StoryblokPrivateCustomerToggle({
  isB2BCustomer,
  userData,
  isMobileVisible = false,
  variant = "default",
}: {
  isB2BCustomer: boolean | undefined
  userData: UserData | undefined
  isMobileVisible?: boolean
  variant?: "default" | "rebrush"
}) {
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const { setIsLoading, setBasketData } = useUbStorefrontState()

  const t = useTranslations("storefront")

  function updateSignupPathAfterCustomerTypeSwitch(url: URL) {
    const signupPathMatch = url.pathname.match(/^(\/fr\/(?:CHF|EUR))?\/auth\/signup(?:\/(?:b2b|b2c))?\/?$/)
    if (!signupPathMatch) {
      return
    }

    const prefix = signupPathMatch[1] ?? ""
    url.pathname = `${prefix}/auth/signup/${isB2BCustomer ? "b2c" : "b2b"}`
  }

  function handleOnClose() {
    setDialogVisible(false)
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 2000)

    loadRefreshedBaskeAction().then((basketData) => {
      setBasketData(basketData)
      const url = new URL(window.location.href)
      updateSignupPathAfterCustomerTypeSwitch(url)
      //console.log(url.href)
      //UBW-833 remove all combinations of params 'b2c' when switching customer type
      url.searchParams.delete("b2c")
      url.searchParams.delete("B2C")
      url.searchParams.delete("b2C")
      url.searchParams.delete("B2c")
      window.location.href = url.href
    })
  }

  if (!userData && isB2BCustomer === undefined) {
    return (
      <div
        className={cn("animate-pulse rounded bg-gray-200", isMobileVisible ? "h-6 w-44" : "h-6 w-24")}
        aria-hidden="true"
      />
    )
  }

  if (!userData && variant === "rebrush") {
    const customerTypeButtonClassName = `
      inline-flex h-[21px] items-center justify-center gap-2 rounded-full px-2.5 text-xs leading-[12px] font-normal
      focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden
    `
    const customerTypeLabelClassName = "inline-flex items-center justify-center leading-[12px]"

    return (
      <>
        {dialogVisible && <StoryblokCustomerTypeDialogClient onClose={handleOnClose} />}
        <div className="inline-flex items-center rounded-full border border-[#1C333B] bg-[#1C333B]">
          <button
            type="button"
            aria-pressed={!!isB2BCustomer}
            onClick={() => {
              if (!isB2BCustomer) setDialogVisible(true)
            }}
            className={cn(
              customerTypeButtonClassName,
              isB2BCustomer
                ? "bg-[#D1DEE3] text-[#303030] hover:bg-[#B0C2C9]"
                : "bg-transparent text-white hover:bg-[#0F1F24] hover:text-[#DCDCDC]"
            )}
          >
            <span className={cn(customerTypeLabelClassName, "hidden lg:inline-flex")}>{t("customer.b2b")}</span>
            <span className={cn(customerTypeLabelClassName, "lg:hidden")}>Geschäft</span>
          </button>
          <button
            type="button"
            aria-pressed={!isB2BCustomer}
            onClick={() => {
              if (isB2BCustomer) setDialogVisible(true)
            }}
            className={cn(
              customerTypeButtonClassName,
              !isB2BCustomer
                ? "bg-[#D1DEE3] text-[#303030] hover:bg-[#B0C2C9]"
                : "bg-transparent text-white hover:bg-[#0F1F24] hover:text-[#DCDCDC]"
            )}
          >
            <span className={cn(customerTypeLabelClassName, "hidden lg:inline-flex")}>{t("customer.b2c")}</span>
            <span className={cn(customerTypeLabelClassName, "lg:hidden")}>Privat</span>
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {dialogVisible && <StoryblokCustomerTypeDialogClient onClose={handleOnClose} />}

      {!userData &&
        (isMobileVisible ? (
          <div className="flex items-center space-x-2">
            <Label
              htmlFor="business-customer-mode"
              className={cn("tracking-tight xl:tracking-normal", {
                "font-bold": isB2BCustomer,
              })}
            >
              {t("customer.b2b")}
            </Label>
            <Switch
              aria-labelledby="business-customer-mode"
              id="business-customer-mode"
              checked={!isB2BCustomer}
              onCheckedChange={() => setDialogVisible(true)}
            />
            <Label
              htmlFor="business-customer-mode"
              className={cn("tracking-tight xl:tracking-normal", {
                "font-bold": !isB2BCustomer,
              })}
            >
              {t("customer.b2c")}
            </Label>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Select value={isB2BCustomer ? "b2b" : "b2c"} onValueChange={() => setDialogVisible(true)}>
              <SelectTrigger
                className="h-6 border-none p-0 text-xs font-bold text-black"
                aria-label={t("customer.b2b") + " | " + t("customer.b2c")}
              >
                {isB2BCustomer ? t("customer.b2b") : t("customer.b2c")}
              </SelectTrigger>
              <SelectContent className={"bg-white"}>
                {!isB2BCustomer && (
                  <SelectItem className="bg-white p-1 text-xs font-bold" value="b2b" aria-label={t("customer.b2b")}>
                    {t("customer.b2b")}
                  </SelectItem>
                )}
                {isB2BCustomer && (
                  <SelectItem className="bg-white p-1 text-xs font-bold" value="b2c" aria-label={t("customer.b2c")}>
                    {t("customer.b2c")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        ))}
    </>
  )
}
